import React, { useState } from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { useNavigate } from "react-router-dom";
import SignInNavBarBack from "components/layout/navigation/SignInNavBarBack/SignInNavBarBack";
import BenevvTextField from "components/forms/BenevvTextField/BenevvTextField";
import BenevvSubmitField from "components/forms/BenevvSubmitField/BenevvSubmitField";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Avatar,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  Slide,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { RedTextBold, GreyTextLight } from "components/text/Text";
import { updateSignUpScreen } from "store/ui/signUpScreen/signUpScreen.slice";
import { updateAuthentication } from "store/auth/auth.slice";
import { updateUser } from "store/user/user.slice";
import * as AuthServiceApi from "apis/AuthServiceApi";
import * as UserServiceApi from "apis/UserServiceApi";
import "./SignIn.css";

interface Organization {
  organization_id: number;
  name: string;
  socialAvatarUrl?: string;
}

interface LoginUserResponse {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
  userType: string;
  templateType: string;
  organizations?: Organization[];
  userId?: number;
  role?: string;
}

function SignIn() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const history: string[] = useAppSelector(
    (state) => state.signUpScreen.history
  );

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isInvalid, setIsInvalid] = useState(false);
  const [isOrg, setIsOrg] = useState(false);
  const [userResponse, setUserResponse] = useState<LoginUserResponse | null>(
    null
  );
  const [showOrgDialog, setShowOrgDialog] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload: {
      Email_address: string;
      Password: string;
    } = {
      Email_address: email,
      Password: password,
    };
    try {
      const result = (await AuthServiceApi.loginUser(
        payload
      )) as LoginUserResponse;
      dispatch(
        updateAuthentication({
          accessToken: result.accessToken,
          tokenType: result.tokenType,
          expiresIn: result.expiresIn,
          userType: result.userType,
          templateType: result.templateType,
          role: result.role,
          organizations: result.organizations ?? [],
          personalSession: {
            accessToken: result.accessToken,
            tokenType: result.tokenType,
            expiresIn: result.expiresIn,
            userId: result.userId!,
            userType: result.userType,
            templateType: result.templateType,
            role: result.role,
          },

          currentSession: "myself",
        })
      );
      // if (result.organizations?.length) {
      //   setUserResponse(result);
      //   setShowOrgDialog(true);
      // } else {
      finishLoginWithoutOrganization(result);
      // }
    } catch (ex) {
      setIsInvalid(true);
      console.error(ex);
    }
  };

  const handleCloseDialog = () => {
    setShowOrgDialog(false);
    finishLoginWithoutOrganization();
  };

  const handleSelectOrg = async (org: Organization) => {
    try {
      const orgResponse = await AuthServiceApi.loginOrganization(
        org.organization_id
      );
      dispatch(
        updateAuthentication({
          accessToken: orgResponse.accessToken,
          tokenType: orgResponse.tokenType,
          expiresIn: orgResponse.expiresIn,
          userId: orgResponse.userId,
          userType: orgResponse.userType,
          templateType: orgResponse.templateType,
          role: orgResponse.role,
        })
      );
      const userInfo = await UserServiceApi.getUserByUserId(orgResponse.userId);
      dispatch(updateUser(userInfo));
      setShowOrgDialog(false);
      navigate("/org/home");
    } catch (e) {
      console.error(e);
      setShowOrgDialog(false);
    }
  };

  const finishLoginWithoutOrganization = async (r?: LoginUserResponse) => {
    if (r?.userId) {
      dispatch(updateAuthentication({ userId: r.userId }));
      const userInfo = await UserServiceApi.getUserByUserId(r.userId);
      dispatch(updateUser(userInfo));
      dispatch(
        updateAuthentication({
          personalProfile: {
            id: userInfo.id,
            name: userInfo.name,
            avatar: userInfo.socialAvatarUrl,
          },
        })
      );
      console.log("User info fetched successfully:", userInfo);
    }
    navigate("/org/home");
  };

  const toggleSwitch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsOrg(event.target.checked);
  };

  const renderOrgDialog = () => {
    if (!showOrgDialog || !userResponse?.organizations) return null;
    return (
      <Dialog
        open={showOrgDialog}
        onClose={handleCloseDialog}
        className="custom-dialog"
        fullWidth
        maxWidth="sm"
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: 8,
          },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            bgcolor: "#FF2D55",
            color: "#FFF",
            position: "relative",
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
          }}
        >
          Select an Organization
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "#FFF",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "24px" }}>
          <Typography variant="body2" sx={{ m: "10px 0", color: "#666" }}>
            Please choose one of your organizations below to continue.
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={2} justifyContent="center">
            {userResponse.organizations.map((org) => (
              <Card
                key={org.organization_id}
                sx={{
                  width: 220,
                  borderRadius: 4,
                  boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
                }}
              >
                <CardActionArea
                  onClick={() => handleSelectOrg(org)}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    p: 2,
                    alignItems: "center",
                  }}
                >
                  <Avatar
                    src={org.socialAvatarUrl}
                    alt={org.name}
                    sx={{
                      width: 64,
                      height: 64,
                      mb: 1,
                      objectFit: "cover",
                    }}
                  />
                  <CardContent sx={{ p: 0 }}>
                    <Typography
                      variant="subtitle1"
                      align="center"
                      sx={{ fontWeight: "bold" }}
                    >
                      {org.name}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <div className="sign-up-right-container">
      <SignInNavBarBack screen={history[history.length - 2]} />
      <Slide direction="left" in={true} mountOnEnter unmountOnExit>
        <div className="sign-in">
          <div className="sign-in-text">
            <h1>Welcome to BENEVV!</h1>
            <p className="sign-in-subtext">A Social Giving Community</p>
            <p className="sign-in-subtext-causes">
              Keep your mind and body connected to those causes which need your
              attention
            </p>
          </div>
          <form className="sign-in-form" onSubmit={onSubmit}>
            <BenevvTextField
              htmlFor="email"
              labelText="Email address*"
              placeholder="Enter email address"
              required
              setTextField={setEmail}
            />
            <BenevvTextField
              htmlFor="password"
              labelText="Password*"
              placeholder="Enter password"
              required
              setTextField={setPassword}
              password
            />
            {isInvalid && (
              <p style={{ color: "red" }}>
                Your Email address or Password is incorrect.
              </p>
            )}
            {/* <div className="toggle-container">
              <span
                className={`toggle-label ${!isOrg ? "active" : ""}`}
                onClick={() => setIsOrg(false)}
              >
                User
              </span>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={isOrg}
                  onChange={toggleSwitch}
                />
                <span className="slider round"></span>
              </label>
              <span
                className={`toggle-label ${isOrg ? "active" : ""}`}
                onClick={() => setIsOrg(true)}
              >
                Organization
              </span>
            </div> */}
            <div className="sign-in-questions-text">
              <div className="dont-have-an-account-text-container">
                <GreyTextLight>Don&apos;t have an account?&nbsp;</GreyTextLight>
                <RedTextBold
                  onClick={() => dispatch(updateSignUpScreen("SignUpScreen"))}
                >
                  Sign Up
                </RedTextBold>
              </div>
              <p
                className="sign-in-forgot-text"
                onClick={() =>
                  dispatch(updateSignUpScreen("SignInForgotPasswordScreen"))
                }
              >
                Forgot password?
              </p>
            </div>
            <BenevvSubmitField value="Sign In" style={{ width: "100%" }} />
          </form>
        </div>
      </Slide>
      {renderOrgDialog()}
    </div>
  );
}

export default SignIn;
